import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Mic,
  MicOff,
  Upload,
  FileText,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Shield,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
  Loader2,
  X,
  Plus,
  Users,
  Link2,
  FileCheck,
  AlertCircle,
  Send,
  MessageSquare,
  Sparkles,
  Clock,
  Star,
  Navigation,
  ClipboardList
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import ServiceRequestForm from '../intake/ServiceRequestForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CareHub - Caregiver & Client Onboarding Center
 * 
 * 3 Input Methods:
 * 1. Manual - Traditional form entry
 * 2. Voice - Field-by-field guided dictation
 * 3. Document Upload - Scan and extract (OCR)
 * 
 * Features:
 * - Duplicate detection with warnings
 * - Field validation with error highlighting
 * - HIPAA consent before document upload
 * - Caregiver-Client linking (bidirectional)
 * - Client Intake workflow (Service Request → Assessment → Care Plan)
 * - AI-powered caregiver recommendations for clients
 */

const ENTRY_MODES = [
  { id: 'manual', label: 'Manual Entry', icon: FileText, description: 'Type information into form fields' },
  { id: 'voice', label: 'Voice Dictation', icon: Mic, description: 'Speak field-by-field with guided prompts' },
  { id: 'upload', label: 'Document Upload', icon: Upload, description: 'Upload and scan documents' },
];

const RECORD_TYPES = [
  { id: 'caregiver', label: 'Caregiver', icon: User, description: 'Add a new or existing caregiver', color: 'blue' },
  { id: 'client', label: 'Client', icon: Users, description: 'Add a new or existing client', color: 'emerald' },
  { id: 'intake', label: 'New Client Intake', icon: ClipboardList, description: 'Start full intake workflow with assessment', color: 'purple' },
];

const CAREGIVER_DOCUMENT_TYPES = [
  { key: 'drivers_license', label: "Driver's License / State ID", required: true },
  { key: 'social_security', label: 'Social Security Card', required: true },
  { key: 'cna_certification', label: 'CNA Certification', required: false },
  { key: 'hha_certification', label: 'HHA Certification', required: false },
  { key: 'cpr_certification', label: 'CPR Certification', required: false },
  { key: 'tb_test', label: 'TB Test Results', required: false },
  { key: 'car_insurance', label: 'Car Insurance', required: false },
  { key: 'car_registration', label: 'Car Registration', required: false },
  { key: 'guardian_registration', label: 'Guardian Registration', required: false },
  { key: 'resume', label: 'Resume', required: false },
  { key: 'background_check', label: 'Background Check', required: false },
  { key: 'other', label: 'Other Document', required: false },
];

const CLIENT_DOCUMENT_TYPES = [
  { key: 'photo_id', label: 'Photo ID', required: false },
  { key: 'insurance_card', label: 'Insurance Card', required: false },
  { key: 'va_authorization', label: 'VA Authorization', required: false },
  { key: 'triwest_authorization', label: 'TriWest Authorization', required: false },
  { key: 'medicaid_card', label: 'Medicaid Card', required: false },
  { key: 'power_of_attorney', label: 'Power of Attorney', required: false },
  { key: 'advance_directive', label: 'Advance Directive', required: false },
  { key: 'physician_orders', label: 'Physician Orders', required: false },
  { key: 'care_plan', label: 'Care Plan', required: false },
  { key: 'other', label: 'Other Document', required: false },
];

// Get document types based on record type
const getDocumentTypes = (recordType) => {
  return recordType === 'client' ? CLIENT_DOCUMENT_TYPES : CAREGIVER_DOCUMENT_TYPES;
};

// Legacy alias for backwards compatibility
const DOCUMENT_TYPES = CAREGIVER_DOCUMENT_TYPES;

const HIPAA_CONSENT_TEXT = `I acknowledge that I have read and understand the HIPAA Privacy Notice. I consent to the collection, use, and disclosure of my personal health information as described. I understand that my information will be kept secure and confidential in accordance with federal and state privacy laws.

By checking the box below, I confirm that:
• I am providing this information voluntarily
• I understand my rights under HIPAA
• I authorize the secure storage and processing of my documents
• I understand I can revoke this consent at any time in writing`;

export default function CareHub() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [mode, setMode] = useState(null);
  const [step, setStep] = useState('select-type'); // select-type, select-mode, entry, review, complete
  const [recordType, setRecordType] = useState(null); // caregiver, client, or intake
  
  // Check URL params for pre-selected mode
  useEffect(() => {
    const modeParam = searchParams.get('mode');
    if (modeParam === 'client') {
      setRecordType('client');
      setStep('select-mode');
    } else if (modeParam === 'caregiver') {
      setRecordType('caregiver');
      setStep('select-mode');
    } else if (modeParam === 'intake') {
      setRecordType('intake');
      setShowIntakeForm(true);
      setStep('select-mode');
    }
  }, [searchParams]);
  
  // Intake flow state
  const [showIntakeForm, setShowIntakeForm] = useState(false);
  const [intakeServiceRequest, setIntakeServiceRequest] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [warnings, setWarnings] = useState({});
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  
  // Voice state
  const [isRecording, setIsRecording] = useState(false);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [fieldPrompts, setFieldPrompts] = useState([]);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Document state
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  // HIPAA consent
  const [hipaaConsented, setHipaaConsented] = useState(false);
  const [showHipaaModal, setShowHipaaModal] = useState(false);
  
  // Profile state
  const [profileId, setProfileId] = useState(null);
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [sendWelcome, setSendWelcome] = useState(true);  // Send welcome email/SMS
  const [welcomeSent, setWelcomeSent] = useState(null);  // Track communication results
  
  // Client assignment (when adding caregivers)
  const [availableClients, setAvailableClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  
  // Caregiver assignment (when adding clients)
  const [availableCaregivers, setAvailableCaregivers] = useState([]);
  const [selectedCaregivers, setSelectedCaregivers] = useState([]);
  
  // AI Recommendations
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);

  useEffect(() => {
    if (recordType) {
      fetchFieldPrompts();
    }
    if (recordType === 'caregiver') {
      fetchAvailableClients();
    }
    if (recordType === 'client') {
      fetchAvailableCaregivers();
    }
  }, [recordType]);

  const fetchFieldPrompts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/data-entry/field-prompts`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFieldPrompts(data[recordType] || []);
      }
    } catch (err) {
      console.error('Error fetching field prompts:', err);
    }
  };

  const fetchAvailableClients = async () => {
    try {
      const res = await fetch(`${API_URL}/api/clients?status=active&limit=100`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAvailableClients(data.items || data || []);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const fetchAvailableCaregivers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/data-entry/caregivers?status=active&limit=100`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setAvailableCaregivers(data.items || data || []);
      }
    } catch (err) {
      console.error('Error fetching caregivers:', err);
    }
  };

  // AI-powered caregiver recommendation
  const fetchAiRecommendations = async () => {
    if (!formData.address_line1 || !formData.city) {
      toast.error('Please enter the client address first for location-based recommendations');
      return;
    }
    
    setLoadingRecommendations(true);
    setShowAiPanel(true);
    
    try {
      const res = await fetch(`${API_URL}/api/data-entry/recommend-caregivers`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_address: `${formData.address_line1}, ${formData.city}, ${formData.state} ${formData.zip_code}`,
          client_city: formData.city,
          client_state: formData.state,
          care_needs: formData.notes || '',
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setAiRecommendations(data.recommendations || []);
        
        if (data.recommendations?.length === 0) {
          toast.info('No caregivers available matching criteria. Showing all active caregivers.');
        }
      } else {
        // Fallback to showing available caregivers sorted by name
        toast.info('Showing available caregivers');
        setAiRecommendations(availableCaregivers.map(cg => ({
          ...cg,
          match_score: null,
          match_reasons: ['Available caregiver']
        })));
      }
    } catch (err) {
      console.error('Error fetching recommendations:', err);
      toast.error('Could not fetch recommendations');
      // Fallback
      setAiRecommendations(availableCaregivers.map(cg => ({
        ...cg,
        match_score: null,
        match_reasons: ['Available caregiver']
      })));
    } finally {
      setLoadingRecommendations(false);
    }
  };

  // Auto-fetch recommendations when client address is complete
  useEffect(() => {
    if (recordType === 'client' && formData.address_line1 && formData.city && formData.state && step === 'entry') {
      // Debounce the auto-fetch
      const timer = setTimeout(() => {
        if (aiRecommendations.length === 0 && !loadingRecommendations) {
          fetchAiRecommendations();
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [formData.address_line1, formData.city, formData.state, recordType, step]);

  const handleModeSelect = (selectedMode) => {
    setMode(selectedMode);
    if (selectedMode === 'upload') {
      setShowHipaaModal(true);
    } else {
      setStep('entry');
    }
  };

  const handleRecordTypeSelect = (type) => {
    if (type === 'intake') {
      // Show the full intake form
      setShowIntakeForm(true);
      return;
    }
    setRecordType(type);
    setStep('select-mode');
    // Reset form data when switching record types
    setFormData({});
    setErrors({});
    setWarnings({});
    setDuplicateWarning(null);
  };

  const handleIntakeComplete = (serviceRequest) => {
    setIntakeServiceRequest(serviceRequest);
    setShowIntakeForm(false);
    // Navigate to Schedule Assessment step
    toast.success('Service request created! Proceeding to schedule assessment...');
    navigate(`/intake/schedule/${serviceRequest.id}`);
  };

  const handleIntakeCancel = () => {
    setShowIntakeForm(false);
    setStep('select-type');
  };

  const handleHipaaConsent = () => {
    setHipaaConsented(true);
    setShowHipaaModal(false);
    setStep('entry');
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateField = async (field, value) => {
    try {
      const res = await fetch(`${API_URL}/api/data-entry/validate?field_name=${field}&value=${encodeURIComponent(value)}&record_type=${recordType}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const result = await res.json();
        if (!result.is_valid) {
          setErrors(prev => ({ ...prev, [field]: result.errors[0]?.message }));
        }
        if (result.warnings?.length > 0) {
          setWarnings(prev => ({ ...prev, [field]: result.warnings[0]?.message }));
        }
      }
    } catch (err) {
      console.error('Validation error:', err);
    }
  };

  const checkDuplicates = async () => {
    if (!formData.first_name || !formData.last_name) return;
    
    try {
      const params = new URLSearchParams({
        first_name: formData.first_name,
        last_name: formData.last_name,
        record_type: recordType
      });
      if (formData.email) params.append('email', formData.email);
      if (formData.phone) params.append('phone', formData.phone);
      
      const res = await fetch(`${API_URL}/api/data-entry/check-duplicate?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (res.ok) {
        const result = await res.json();
        if (result.has_duplicates) {
          setDuplicateWarning(result);
        } else {
          setDuplicateWarning(null);
        }
      }
    } catch (err) {
      console.error('Duplicate check error:', err);
    }
  };

  // Voice Recording Functions - Cross-browser compatible (Android + iOS)
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Detect supported MIME type (Safari uses mp4, Chrome uses webm)
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/mpeg')) {
        mimeType = 'audio/mpeg';
      } else if (MediaRecorder.isTypeSupported('audio/wav')) {
        mimeType = 'audio/wav';
      }
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        // Get file extension from mime type
        const ext = mimeType.split('/')[1].replace('mpeg', 'mp3');
        await transcribeAudio(audioBlob, ext);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Error starting recording:', err);
      alert('Could not access microphone. Please check permissions and try again.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob, ext = 'webm') => {
    setTranscribing(true);
    const currentField = fieldPrompts[currentFieldIndex];
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('audio', audioBlob, `recording.${ext}`);
      formDataUpload.append('field_name', currentField?.field || '');
      
      const res = await fetch(`${API_URL}/api/data-entry/transcribe`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formDataUpload
      });
      
      if (res.ok) {
        const result = await res.json();
        handleFieldChange(currentField.field, result.processed_value);
        // Auto-advance to next field
        if (currentFieldIndex < fieldPrompts.length - 1) {
          setCurrentFieldIndex(prev => prev + 1);
        }
      } else {
        const error = await res.json();
        alert(`Transcription failed: ${error.detail}`);
      }
    } catch (err) {
      console.error('Transcription error:', err);
      alert('Failed to transcribe audio. Please try again.');
    } finally {
      setTranscribing(false);
    }
  };

  // Document Upload
  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!hipaaConsented) {
      setShowHipaaModal(true);
      return;
    }
    
    setUploadingDoc(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('document_type', docType);
      
      if (profileId) {
        const res = await fetch(`${API_URL}/api/data-entry/caregivers/${profileId}/documents`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: formDataUpload
        });
        
        if (res.ok) {
          const result = await res.json();
          setDocuments(prev => [...prev, result.document]);
          setProfileCompletion(result.profile_completion);
        }
      } else {
        // Store locally until profile is created
        setDocuments(prev => [...prev, {
          document_type: docType,
          file_name: file.name,
          file_size: file.size,
          file: file,
          uploaded_at: new Date().toISOString()
        }]);
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload document. Please try again.');
    } finally {
      setUploadingDoc(false);
    }
  };

  // Save Profile
  const saveProfile = async () => {
    // Validate required fields based on record type
    const requiredFields = recordType === 'client' 
      ? ['first_name', 'last_name', 'address_line1', 'city', 'state', 'zip_code']
      : ['first_name', 'last_name'];
    const missingFields = requiredFields.filter(f => !formData[f]);
    
    if (missingFields.length > 0) {
      setErrors(prev => ({
        ...prev,
        ...Object.fromEntries(missingFields.map(f => [f, 'This field is required']))
      }));
      return;
    }
    
    // Require email if sending welcome (only for caregivers)
    if (recordType === 'caregiver' && sendWelcome && !formData.email) {
      setErrors(prev => ({ ...prev, email: 'Email is required to send login credentials' }));
      return;
    }
    
    setSaving(true);
    
    try {
      // Check duplicates first
      await checkDuplicates();
      
      if (recordType === 'client') {
        // Save Client
        await saveClient();
      } else {
        // Save Caregiver
        await saveCaregiver();
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Save Client Profile
  const saveClient = async () => {
    // Build address from components
    const address = [
      formData.address_line1,
      formData.address_line2,
      `${formData.city}, ${formData.state} ${formData.zip_code}`
    ].filter(Boolean).join(', ');

    const clientData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      date_of_birth: formData.date_of_birth,
      phone: formData.phone,
      address: address,
      address_line1: formData.address_line1,
      address_line2: formData.address_line2,
      city: formData.city,
      state: formData.state,
      zip_code: formData.zip_code,
      emergency_contact_name: formData.emergency_contact_name,
      emergency_contact_phone: formData.emergency_contact_phone,
      notes: formData.notes,
      entry_method: mode,
    };

    if (profileId) {
      // Update existing client
      const res = await fetch(`${API_URL}/api/clients/${profileId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(clientData)
      });
      
      if (res.ok) {
        setStep('complete');
      } else {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to update client');
      }
    } else {
      // Create new client
      const res = await fetch(`${API_URL}/api/data-entry/clients`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(clientData)
      });
      
      if (res.ok) {
        const result = await res.json();
        setProfileId(result.client?.id || result.id);
        
        if (result.duplicate_warning) {
          setDuplicateWarning(result.duplicate_warning);
        }
        
        // Upload any pending documents
        if (documents.length > 0 && (result.client?.id || result.id)) {
          for (const doc of documents) {
            if (doc.file) {
              const formDataDoc = new FormData();
              formDataDoc.append('file', doc.file);
              formDataDoc.append('document_type', doc.document_type);
              
              await fetch(`${API_URL}/api/data-entry/clients/${result.client?.id || result.id}/documents`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formDataDoc
              });
            }
          }
        }
        
        // Assign caregivers if selected
        if (selectedCaregivers.length > 0) {
          const clientId = result.client?.id || result.id;
          await fetch(`${API_URL}/api/data-entry/clients/${clientId}/assign-caregivers`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(selectedCaregivers)
          });
        }
        
        setProfileCompletion(100);
        setStep('complete');
      } else {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to create client');
      }
    }
  };

  // Save Caregiver Profile
  const saveCaregiver = async () => {
    if (profileId) {
      // Update existing profile
      const res = await fetch(`${API_URL}/api/data-entry/caregivers/${profileId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...formData, entry_method: mode })
      });
      
      if (res.ok) {
        const updated = await res.json();
        setProfileCompletion(updated.profile_completion_percent);
        
        // Assign clients if selected
        if (selectedClients.length > 0) {
          await fetch(`${API_URL}/api/data-entry/caregivers/${profileId}/assign-clients`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(selectedClients)
          });
        }
        
        setStep('complete');
      } else {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to update caregiver');
      }
    } else {
      // Create new profile WITH user account and welcome communications
      const endpoint = sendWelcome 
        ? `${API_URL}/api/data-entry/caregivers/onboard`
        : `${API_URL}/api/data-entry/caregivers`;
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          date_of_birth: formData.date_of_birth,
          entry_method: mode,
          send_welcome: sendWelcome
        })
      });
      
      if (res.ok) {
        const result = await res.json();
        setProfileId(result.profile.id);
        
        if (result.duplicate_warning) {
          setDuplicateWarning(result.duplicate_warning);
        }
        
        // Track welcome communications result
        if (result.communications) {
          setWelcomeSent(result.communications);
        }
        
        // Update with remaining fields if needed
        const additionalFields = { ...formData };
        delete additionalFields.first_name;
        delete additionalFields.last_name;
        delete additionalFields.email;
        delete additionalFields.phone;
        delete additionalFields.date_of_birth;
        
        if (Object.keys(additionalFields).length > 0) {
          await fetch(`${API_URL}/api/data-entry/caregivers/${result.profile.id}`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(additionalFields)
          });
        }
        
        // Record HIPAA consent if given
        if (hipaaConsented) {
          await fetch(`${API_URL}/api/data-entry/caregivers/${result.profile.id}/hipaa-consent`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ consented: true })
          });
        }
        
        // Assign clients if selected
        if (selectedClients.length > 0) {
          await fetch(`${API_URL}/api/data-entry/caregivers/${result.profile.id}/assign-clients`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(selectedClients)
          });
        }
        
        setProfileCompletion(result.profile.profile_completion_percent);
        setStep('complete');
      } else {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to save caregiver');
      }
    }
  };

  // Render Intake Form (Service Request)
  if (showIntakeForm) {
    return (
      <ServiceRequestForm
        onComplete={handleIntakeComplete}
        onCancel={handleIntakeCancel}
      />
    );
  }

  // Render Record Type Selection
  if (step === 'select-type') {
    return (
      <div className="min-h-screen bg-[#FBFBFA]">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#176DFF] to-[#29C0FF] flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-semibold text-[#0D1B33]">CareHub</h1>
            </div>
            <p className="text-[#64748B]">What would you like to do?</p>
          </header>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {RECORD_TYPES.map((type) => {
              const colorClasses = {
                blue: 'border-blue-200 hover:border-blue-400',
                emerald: 'border-emerald-200 hover:border-emerald-400',
                purple: 'border-purple-200 hover:border-purple-400',
              };
              const bgClasses = {
                blue: 'bg-blue-100 group-hover:bg-blue-200',
                emerald: 'bg-emerald-100 group-hover:bg-emerald-200',
                purple: 'bg-purple-100 group-hover:bg-purple-200',
              };
              const iconClasses = {
                blue: 'text-blue-600',
                emerald: 'text-emerald-600',
                purple: 'text-purple-600',
              };
              
              return (
                <button
                  key={type.id}
                  onClick={() => handleRecordTypeSelect(type.id)}
                  className={`bg-white rounded-xl border-2 p-6 hover:shadow-lg transition-all text-left group ${colorClasses[type.color]}`}
                  data-testid={`record-type-${type.id}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${bgClasses[type.color]}`}>
                    <type.icon className={`w-6 h-6 ${iconClasses[type.color]}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0D1B33] mb-1">{type.label}</h3>
                  <p className="text-sm text-[#64748B]">{type.description}</p>
                </button>
              );
            })}
          </div>
          
          <div className="bg-blue-50 rounded-lg p-4 flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">HIPAA Compliant</p>
              <p className="text-sm text-blue-700">All data is encrypted and stored securely in compliance with federal privacy regulations.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Mode Selection
  if (step === 'select-mode') {
    const typeLabel = recordType === 'client' ? 'Client' : 'Caregiver';
    const typeColor = recordType === 'client' ? 'emerald' : 'blue';
    
    return (
      <div className="min-h-screen bg-[#FBFBFA]">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <button
            onClick={() => {
              setStep('select-type');
              setRecordType(null);
            }}
            className="flex items-center gap-2 text-[#64748B] hover:text-[#0D1B33] mb-6 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to type selection
          </button>
          
          <header className="mb-8">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium mb-3 ${
              typeColor === 'blue' 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-emerald-100 text-emerald-700'
            }`}>
              {recordType === 'client' ? <Users className="w-4 h-4" /> : <User className="w-4 h-4" />}
              {typeLabel}
            </div>
            <h1 className="text-2xl font-semibold text-[#0D1B33] mb-2">Add New {typeLabel}</h1>
            <p className="text-[#64748B]">Choose how you&apos;d like to enter the {typeLabel.toLowerCase()}&apos;s information</p>
          </header>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {ENTRY_MODES.map((entryMode) => (
              <button
                key={entryMode.id}
                onClick={() => handleModeSelect(entryMode.id)}
                className={`bg-white rounded-xl border border-[#E2E8F0] p-6 hover:shadow-md transition-all text-left group ${
                  typeColor === 'blue' 
                    ? 'hover:border-blue-300' 
                    : 'hover:border-emerald-300'
                }`}
                data-testid={`entry-mode-${entryMode.id}`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-colors ${
                  typeColor === 'blue'
                    ? 'bg-blue-50 group-hover:bg-blue-100'
                    : 'bg-emerald-50 group-hover:bg-emerald-100'
                }`}>
                  <entryMode.icon className={`w-6 h-6 ${
                    typeColor === 'blue' ? 'text-blue-600' : 'text-emerald-600'
                  }`} />
                </div>
                <h3 className="font-semibold text-[#0D1B33] mb-1">{entryMode.label}</h3>
                <p className="text-sm text-[#64748B]">{entryMode.description}</p>
              </button>
            ))}
          </div>
          
          <div className="bg-blue-50 rounded-lg p-4 flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">HIPAA Compliant</p>
              <p className="text-sm text-blue-700">All data is encrypted and stored securely in compliance with federal privacy regulations.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render HIPAA Consent Modal
  if (showHipaaModal) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-[#E2E8F0]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#0D1B33]">HIPAA Privacy Consent</h2>
                <p className="text-sm text-[#64748B]">Required before uploading documents</p>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            <div className="bg-[#F8FAFC] rounded-lg p-4 mb-6 text-sm text-[#334155] whitespace-pre-line">
              {HIPAA_CONSENT_TEXT}
            </div>
            
            <label className="flex items-start gap-3 cursor-pointer mb-6">
              <input
                type="checkbox"
                checked={hipaaConsented}
                onChange={(e) => setHipaaConsented(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-[#CBD5E1] text-[#176DFF] focus:ring-[#176DFF]"
              />
              <span className="text-sm text-[#334155]">
                I have read and agree to the HIPAA Privacy Notice and consent to the secure handling of my information.
              </span>
            </label>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowHipaaModal(false);
                  setMode(null);
                  setStep('select-mode');
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-[#176DFF] hover:bg-[#176DFF]/90"
                disabled={!hipaaConsented}
                onClick={handleHipaaConsent}
              >
                I Consent — Continue
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Entry Form
  if (step === 'entry') {
    const typeLabel = recordType === 'client' ? 'Client' : 'Caregiver';
    const typeColor = recordType === 'client' ? 'emerald' : 'blue';
    const currentDocTypes = getDocumentTypes(recordType);
    
    return (
      <div className="min-h-screen bg-[#FBFBFA]">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Header */}
          <header className="mb-6">
            <button
              onClick={() => { setStep('select-mode'); setMode(null); }}
              className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0D1B33] mb-4"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to entry methods
            </button>
            <div className="flex items-center justify-between">
              <div>
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium mb-2 ${
                  typeColor === 'blue' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {recordType === 'client' ? <Users className="w-4 h-4" /> : <User className="w-4 h-4" />}
                  {typeLabel}
                </div>
                <h1 className="text-2xl font-semibold text-[#0D1B33] mb-1">
                  {mode === 'manual' && 'Manual Entry'}
                  {mode === 'voice' && 'Voice Dictation'}
                  {mode === 'upload' && 'Document Upload'}
                </h1>
                <p className="text-[#64748B]">Enter {typeLabel.toLowerCase()} information</p>
              </div>
              <div className="text-right">
                <div className={`text-2xl font-semibold ${typeColor === 'blue' ? 'text-blue-600' : 'text-emerald-600'}`}>{profileCompletion}%</div>
                <div className="text-xs text-[#64748B]">Profile Complete</div>
              </div>
            </div>
          </header>
          
          {/* Duplicate Warning */}
          {duplicateWarning && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">Possible Duplicate Detected</p>
                  <p className="text-sm text-amber-700 mb-2">
                    A similar record already exists. Please verify before continuing.
                  </p>
                  <div className="space-y-2">
                    {duplicateWarning.duplicates.map((dup, i) => (
                      <div key={i} className="text-sm bg-white rounded p-2 border border-amber-200">
                        <span className="font-medium">{dup.name}</span>
                        {dup.email && <span className="text-[#64748B]"> — {dup.email}</span>}
                        <div className="text-xs text-amber-600 mt-1">
                          Match: {dup.match_reasons.join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Voice Mode */}
          {mode === 'voice' && (
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 mb-6">
              <div className="text-center mb-6">
                <p className="text-lg font-medium text-[#0D1B33] mb-2">
                  {fieldPrompts[currentFieldIndex]?.prompt || 'Loading...'}
                </p>
                <p className="text-sm text-[#64748B]">
                  Field {currentFieldIndex + 1} of {fieldPrompts.length}
                </p>
              </div>
              
              <div className="flex justify-center mb-6">
                <button
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={transcribing}
                  className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
                    isRecording 
                      ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                      : 'bg-[#176DFF] hover:bg-[#176DFF]/90'
                  } ${transcribing ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {transcribing ? (
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  ) : isRecording ? (
                    <MicOff className="w-8 h-8 text-white" />
                  ) : (
                    <Mic className="w-8 h-8 text-white" />
                  )}
                </button>
              </div>
              
              <p className="text-center text-sm text-[#64748B]">
                {transcribing ? 'Transcribing...' : isRecording ? 'Tap to stop recording' : 'Tap to start recording'}
              </p>
              
              {formData[fieldPrompts[currentFieldIndex]?.field] && (
                <div className="mt-4 p-3 bg-[#F8FAFC] rounded-lg">
                  <p className="text-sm text-[#64748B]">Recognized:</p>
                  <p className="font-medium text-[#0D1B33]">
                    {formData[fieldPrompts[currentFieldIndex]?.field]}
                  </p>
                </div>
              )}
              
              <div className="flex justify-between mt-6">
                <Button
                  variant="outline"
                  disabled={currentFieldIndex === 0}
                  onClick={() => setCurrentFieldIndex(prev => prev - 1)}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (currentFieldIndex < fieldPrompts.length - 1) {
                      setCurrentFieldIndex(prev => prev + 1);
                    }
                  }}
                >
                  Skip
                </Button>
                <Button
                  disabled={currentFieldIndex === fieldPrompts.length - 1}
                  onClick={() => setCurrentFieldIndex(prev => prev + 1)}
                  className="bg-[#176DFF] hover:bg-[#176DFF]/90"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Manual Form / Collected Data Display */}
          <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
            <div className="px-5 py-4 border-b border-[#E2E8F0]">
              <h2 className="font-semibold text-[#0D1B33]">Personal Information</h2>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                label="First Name"
                field="first_name"
                value={formData.first_name || ''}
                onChange={handleFieldChange}
                onBlur={() => checkDuplicates()}
                error={errors.first_name}
                required
              />
              <FormField
                label="Last Name"
                field="last_name"
                value={formData.last_name || ''}
                onChange={handleFieldChange}
                onBlur={() => checkDuplicates()}
                error={errors.last_name}
                required
              />
              <FormField
                label="Preferred Name"
                field="preferred_name"
                value={formData.preferred_name || ''}
                onChange={handleFieldChange}
              />
              <FormField
                label="Date of Birth"
                field="date_of_birth"
                type="date"
                value={formData.date_of_birth || ''}
                onChange={handleFieldChange}
              />
              <FormField
                label="Phone"
                field="phone"
                type="tel"
                value={formData.phone || ''}
                onChange={handleFieldChange}
                onBlur={() => { validateField('phone', formData.phone); checkDuplicates(); }}
                error={errors.phone}
                icon={Phone}
              />
              <FormField
                label="Email"
                field="email"
                type="email"
                value={formData.email || ''}
                onChange={handleFieldChange}
                onBlur={() => { validateField('email', formData.email); checkDuplicates(); }}
                error={errors.email}
                icon={Mail}
              />
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
            <div className="px-5 py-4 border-b border-[#E2E8F0]">
              <h2 className="font-semibold text-[#0D1B33]">Address</h2>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <FormField
                  label="Street Address"
                  field="address_line1"
                  value={formData.address_line1 || ''}
                  onChange={handleFieldChange}
                  icon={MapPin}
                />
              </div>
              <FormField
                label="City"
                field="city"
                value={formData.city || ''}
                onChange={handleFieldChange}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="State"
                  field="state"
                  value={formData.state || ''}
                  onChange={handleFieldChange}
                />
                <FormField
                  label="ZIP Code"
                  field="zip_code"
                  value={formData.zip_code || ''}
                  onChange={handleFieldChange}
                  onBlur={() => validateField('zip_code', formData.zip_code)}
                  error={errors.zip_code}
                />
              </div>
            </div>
          </div>
          
          {/* Emergency Contact - For Clients */}
          {recordType === 'client' && (
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
              <div className="px-5 py-4 border-b border-[#E2E8F0]">
                <h2 className="font-semibold text-[#0D1B33]">Emergency Contact</h2>
              </div>
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Contact Name"
                  field="emergency_contact_name"
                  value={formData.emergency_contact_name || ''}
                  onChange={handleFieldChange}
                  required
                />
                <FormField
                  label="Contact Phone"
                  field="emergency_contact_phone"
                  type="tel"
                  value={formData.emergency_contact_phone || ''}
                  onChange={handleFieldChange}
                  icon={Phone}
                  required
                />
                <div className="md:col-span-2">
                  <FormField
                    label="Notes / Special Instructions"
                    field="notes"
                    value={formData.notes || ''}
                    onChange={handleFieldChange}
                    textarea
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Caregiver Assignment - For Clients */}
          {recordType === 'client' && (
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
              <div className="px-5 py-4 border-b border-[#E2E8F0]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-emerald-600" />
                    <h2 className="font-semibold text-[#0D1B33]">Assign Caregiver</h2>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={fetchAiRecommendations}
                    disabled={loadingRecommendations}
                    className="text-[#176DFF] border-[#176DFF]/30 hover:bg-[#176DFF]/5"
                  >
                    {loadingRecommendations ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-1" />
                    )}
                    Ask Leynix
                  </Button>
                </div>
              </div>
              <div className="p-5">
                {/* AI Recommendations Panel */}
                {showAiPanel && (
                  <div className="mb-4 bg-gradient-to-r from-[#176DFF]/5 to-[#29C0FF]/5 rounded-lg p-4 border border-[#176DFF]/20">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-[#176DFF]" />
                      <span className="text-sm font-medium text-[#0D1B33]">Leynix Recommendations</span>
                    </div>
                    
                    {loadingRecommendations ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 text-[#176DFF] animate-spin mr-2" />
                        <span className="text-sm text-[#64748B]">Finding best matches...</span>
                      </div>
                    ) : aiRecommendations.length > 0 ? (
                      <div className="space-y-2">
                        {aiRecommendations.slice(0, 5).map((rec, idx) => (
                          <div
                            key={rec.id}
                            className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer ${
                              selectedCaregivers.includes(rec.id)
                                ? 'border-emerald-300 bg-emerald-50'
                                : 'border-gray-200 bg-white hover:border-[#176DFF]/30'
                            }`}
                            onClick={() => {
                              if (selectedCaregivers.includes(rec.id)) {
                                setSelectedCaregivers(prev => prev.filter(id => id !== rec.id));
                              } else {
                                setSelectedCaregivers(prev => [...prev, rec.id]);
                              }
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                idx === 0 ? 'bg-amber-100' : 'bg-gray-100'
                              }`}>
                                {idx === 0 ? (
                                  <Star className="w-4 h-4 text-amber-600" />
                                ) : (
                                  <User className="w-4 h-4 text-gray-500" />
                                )}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-[#0D1B33]">
                                  {rec.first_name} {rec.last_name}
                                  {idx === 0 && <span className="ml-2 text-xs text-amber-600 font-normal">Best Match</span>}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-[#64748B]">
                                  {rec.match_reasons?.map((reason, i) => (
                                    <span key={i} className="flex items-center gap-1">
                                      {reason.includes('Available') && <Clock className="w-3 h-3" />}
                                      {reason.includes('location') && <Navigation className="w-3 h-3" />}
                                      {reason}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {rec.match_score && (
                                <span className="text-xs font-medium text-[#176DFF] bg-[#176DFF]/10 px-2 py-1 rounded">
                                  {rec.match_score}% match
                                </span>
                              )}
                              {selectedCaregivers.includes(rec.id) && (
                                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-[#64748B] text-center py-2">
                        {formData.address_line1 && formData.city && formData.state
                          ? 'No matching caregivers found. Try adding caregivers first or select manually below.'
                          : 'Enter client address to get personalized recommendations'
                        }
                      </p>
                    )}
                  </div>
                )}
                
                {/* Manual Selection */}
                <div>
                  <p className="text-sm text-[#64748B] mb-3">
                    {showAiPanel ? 'Or select manually:' : 'Select caregiver to assign:'}
                  </p>
                  {availableCaregivers.length === 0 ? (
                    <p className="text-sm text-[#64748B]">No active caregivers available for assignment.</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {availableCaregivers
                        .filter(cg => !aiRecommendations.find(r => r.id === cg.id))
                        .map((caregiver) => (
                        <label
                          key={caregiver.id}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F8FAFC] cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCaregivers.includes(caregiver.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedCaregivers(prev => [...prev, caregiver.id]);
                              } else {
                                setSelectedCaregivers(prev => prev.filter(id => id !== caregiver.id));
                              }
                            }}
                            className="w-4 h-4 rounded border-[#CBD5E1] text-emerald-600 focus:ring-emerald-500"
                          />
                          <User className="w-4 h-4 text-[#64748B]" />
                          <span className="text-sm text-[#0D1B33]">
                            {caregiver.first_name} {caregiver.last_name}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Certifications & Registrations - For Caregivers only */}
          {recordType === 'caregiver' && (
          <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
            <div className="px-5 py-4 border-b border-[#E2E8F0]">
              <h2 className="font-semibold text-[#0D1B33]">Certifications & Registrations</h2>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                label="Guardian Registration Number"
                field="guardian_registration_number"
                value={formData.guardian_registration_number || ''}
                onChange={handleFieldChange}
              />
              <FormField
                label="Guardian Reg. Expiry"
                field="guardian_registration_expiry"
                type="date"
                value={formData.guardian_registration_expiry || ''}
                onChange={handleFieldChange}
              />
              <FormField
                label="CNA Number"
                field="cna_number"
                value={formData.cna_number || ''}
                onChange={handleFieldChange}
              />
              <FormField
                label="CNA Expiry"
                field="cna_expiry"
                type="date"
                value={formData.cna_expiry || ''}
                onChange={handleFieldChange}
              />
              <FormField
                label="CPR Expiry"
                field="cpr_expiry"
                type="date"
                value={formData.cpr_expiry || ''}
                onChange={handleFieldChange}
              />
              <FormField
                label="TB Clearance Date"
                field="tb_clearance_date"
                type="date"
                value={formData.tb_clearance_date || ''}
                onChange={handleFieldChange}
              />
            </div>
          </div>
          )}

          {/* Document Upload Section */}
          {(mode === 'upload' || mode === 'manual') && (
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
              <div className="px-5 py-4 border-b border-[#E2E8F0]">
                <h2 className="font-semibold text-[#0D1B33]">Documents</h2>
              </div>
              <div className="p-5">
                {!hipaaConsented && (
                  <div className="bg-amber-50 rounded-lg p-4 mb-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-900">HIPAA Consent Required</p>
                      <p className="text-sm text-amber-700 mb-2">You must consent to HIPAA privacy terms before uploading documents.</p>
                      <Button size="sm" onClick={() => setShowHipaaModal(true)}>
                        Review & Consent
                      </Button>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {currentDocTypes.map((docType) => {
                    const uploaded = documents.find(d => d.document_type === docType.key);
                    return (
                      <div
                        key={docType.key}
                        className={`border rounded-lg p-3 ${uploaded ? 'border-emerald-200 bg-emerald-50' : 'border-[#E2E8F0]'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {uploaded ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <FileText className="w-4 h-4 text-[#64748B]" />
                            )}
                            <span className="text-sm font-medium text-[#0D1B33]">{docType.label}</span>
                            {docType.required && <span className="text-xs text-red-500">*</span>}
                          </div>
                          {!uploaded && (
                            <label className={`cursor-pointer ${!hipaaConsented ? 'opacity-50 pointer-events-none' : ''}`}>
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => handleDocumentUpload(e, docType.key)}
                                disabled={!hipaaConsented || uploadingDoc}
                              />
                              <span className="text-xs text-[#176DFF] hover:underline">Upload</span>
                            </label>
                          )}
                        </div>
                        {uploaded && (
                          <p className="text-xs text-[#64748B] mt-1 truncate">{uploaded.file_name}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Client Assignment - For Caregivers only */}
          {recordType === 'caregiver' && (
          <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
            <div className="px-5 py-4 border-b border-[#E2E8F0]">
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-[#176DFF]" />
                <h2 className="font-semibold text-[#0D1B33]">Assign to Clients</h2>
              </div>
            </div>
            <div className="p-5">
              {availableClients.length === 0 ? (
                <p className="text-sm text-[#64748B]">No active clients available for assignment.</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {availableClients.map((client) => (
                    <label
                      key={client.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F8FAFC] cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(client.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedClients(prev => [...prev, client.id]);
                          } else {
                            setSelectedClients(prev => prev.filter(id => id !== client.id));
                          }
                        }}
                        className="w-4 h-4 rounded border-[#CBD5E1] text-[#176DFF] focus:ring-[#176DFF]"
                      />
                      <Users className="w-4 h-4 text-[#64748B]" />
                      <span className="text-sm text-[#0D1B33]">
                        {client.first_name} {client.last_name}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Send Welcome Email - For Caregivers only */}
          {recordType === 'caregiver' && !profileId && (
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
              <div className="px-5 py-4 border-b border-[#E2E8F0]">
                <div className="flex items-center gap-2">
                  <Send className="w-4 h-4 text-[#176DFF]" />
                  <h2 className="font-semibold text-[#0D1B33]">Welcome Email</h2>
                </div>
              </div>
              <div className="p-5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendWelcome}
                    onChange={(e) => setSendWelcome(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-[#CBD5E1] text-[#176DFF] focus:ring-[#176DFF]"
                  />
                  <div>
                    <span className="text-sm font-medium text-[#0D1B33]">
                      Create My Leynix account & send welcome email
                    </span>
                    <p className="text-xs text-[#64748B] mt-1">
                      Creates a login for this caregiver and emails them their credentials.
                      They can sign in to My Leynix to view schedules, clock in/out, and more.
                    </p>
                  </div>
                </label>
                
                {sendWelcome && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Mail className="w-4 h-4 text-blue-600 mt-0.5" />
                      <div className="text-xs text-blue-800">
                        <p className="font-medium">Welcome email includes:</p>
                        <p>Login URL, username, temporary password, and first-login instructions</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => { setStep('select-mode'); setMode(null); }}>
              Cancel
            </Button>
            <Button
              onClick={saveProfile}
              disabled={saving || !formData.first_name || !formData.last_name}
              className={recordType === 'client' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#176DFF] hover:bg-[#176DFF]/90'}
              data-testid={recordType === 'client' ? 'save-client-btn' : 'save-caregiver-btn'}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Save {recordType === 'client' ? 'Client' : 'Caregiver'}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Render Complete Step
  if (step === 'complete') {
    return (
      <div className="min-h-screen bg-[#FBFBFA] flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold text-[#0D1B33] mb-2">
              {recordType === 'client' ? 'Client' : 'Caregiver'} Added Successfully
            </h2>
            <p className="text-[#64748B] mb-6">
              {formData.first_name} {formData.last_name}&apos;s profile has been created with {profileCompletion}% completion.
            </p>
            
            {/* Welcome Email Status */}
            {welcomeSent && (
              <div className="bg-blue-50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm font-medium text-blue-900 mb-2">Welcome Email</p>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-800">
                    {welcomeSent.email_result?.status === 'sent' ? (
                      <span className="text-emerald-600 font-medium">Sent to {formData.email} ✓</span>
                    ) : welcomeSent.email_result?.status === 'skipped' ? (
                      <span className="text-amber-600">Not sent — {welcomeSent.email_result?.reason}</span>
                    ) : (
                      <span className="text-red-600">Failed to send</span>
                    )}
                  </span>
                </div>
                {welcomeSent.email_result?.status === 'sent' && (
                  <p className="text-xs text-blue-700 mt-2">
                    {formData.first_name} can now sign in to My Leynix with their temporary password.
                  </p>
                )}
              </div>
            )}
            
            {profileCompletion < 80 && (
              <div className="bg-amber-50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm font-medium text-amber-900 mb-1">Profile Incomplete</p>
                <p className="text-sm text-amber-700">Add more information to activate this caregiver for scheduling.</p>
              </div>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setFormData({});
                  setDocuments([]);
                  setProfileId(null);
                  setProfileCompletion(0);
                  setSelectedClients([]);
                  setWelcomeSent(null);
                  setStep('select-mode');
                  setMode(null);
                }}
              >
                Add Another
              </Button>
              <Button
                className="flex-1 bg-[#176DFF] hover:bg-[#176DFF]/90"
                onClick={() => navigate('/people/caregivers')}
              >
                View Caregivers
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// Form Field Component
function FormField({ label, field, value, onChange, onBlur, error, warning, type = 'text', required, icon: Icon }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#334155] mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(field, e.target.value)}
          onBlur={onBlur}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#176DFF]/20 focus:border-[#176DFF] ${
            Icon ? 'pl-10' : ''
          } ${error ? 'border-red-300 bg-red-50' : 'border-[#E2E8F0]'}`}
        />
      </div>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      {warning && <p className="text-xs text-amber-600 mt-1">{warning}</p>}
    </div>
  );
}
